import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  fetchStudentNotebook,
  updateNoteTitle,
  updateFlashcardSetTitle,
  updateAudioSummaryTitle,
  updateQuizTitle,
  updateMindMapTitle,
  createNote,
  fetchNote,
  updateNote,
  fetchFlashcards,
  reviewFlashcard,
  fetchAudioSummary,
  fetchQuiz,
  fetchMindMap,
  sendChatMessage,
  generateAudioSummary,
  generateMindMap,
  generateFlashcards,
  generateQuiz,
} from "../../../redux/features/studentNotebookSlice";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import Notification from "../../../components/Notification/Notification";
import userImg from "../../../assets/images/user.png";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-cards";
import { EffectCards } from "swiper/modules";
import Quiz from "../../../components/Quiz/Quiz";
import AudioPlayer from "../../../components/AudioPlayer/AudioPlayer";
import MindMap from "../../../components/MindMap/MindMap";
import {
  AudioIcon,
  QuizIcon,
  FlashcardsIcon,
  MindmapIcon,
  LeftToggleIcon,
  PdfIcon,
  DocIcon,
  RightToggleIcon,
  CloseIcon,
  AudioOverviewIcon,
  MindmapOverviewIcon,
  FlashcardsOverviewIcon,
  QuizOverviewIcon,
  BotIcon,
  SendIcon,
  AddNoteIcon,
  PrevButtonIcon,
  NextButtonIcon,
  TextFileIcon,
  ChevronRight,
} from "../../../components/svgs/Svgs";
import ReactMarkdown from "react-markdown";

const StudentCourseNew = () => {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("id");
  const dispatch = useDispatch();
  const token = useSelector((s) => s.auth.token);
  const {
    documents,
    chatHistory,
    isLoading,
    isNotebookLoading,
    notes,
    flashcardSets,
    audioSummaries,
    quizzes,
    mindMaps,
    notebook,
    user,
    error,
  } = useSelector((s) => s.studentNotebook);

  // Debug log for all items
  console.log("Redux state items:", {
    notes: notes?.length || 0,
    flashcardSets: flashcardSets?.length || 0,
    audioSummaries: audioSummaries?.length || 0,
    quizzes: quizzes?.length || 0,
    mindMaps: mindMaps?.length || 0,
    notebook: notebook,
    user: user,
    userProfileImage: user?.profileImage,
  });

  // Debug log for individual arrays
  console.log("Individual arrays:", {
    notes: notes,
    flashcardSets: flashcardSets,
    audioSummaries: audioSummaries,
    quizzes: quizzes,
    mindMaps: mindMaps,
  });

  // Debug log for sorted items
  useEffect(() => {
    const allItems = [
      ...(notes || []).map((item) => ({ ...item, itemType: "notes" })),
      ...(flashcardSets || []).map((item) => ({
        ...item,
        itemType: "flashcardSets",
      })),
      ...(audioSummaries || []).map((item) => ({
        ...item,
        itemType: "audioSummaries",
      })),
      ...(quizzes || []).map((item) => ({ ...item, itemType: "quizzes" })),
      ...(mindMaps || []).map((item) => ({ ...item, itemType: "mindMaps" })),
    ];

    const sortedItems = allItems.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    console.log(
      "Sorted items (latest first):",
      sortedItems.map((item) => ({
        type: item.itemType,
        title: item.title,
        createdAt: item.createdAt,
        timeAgo: timeAgo(item.createdAt),
      }))
    );
  }, [notes, flashcardSets, audioSummaries, quizzes, mindMaps]);

  useEffect(() => {
    if (token && courseId) {
      dispatch(fetchStudentNotebook({ token, courseId }));
    }
  }, [token, courseId, dispatch]);

  // Initialize local chat history from Redux state
  useEffect(() => {
    if (chatHistory && chatHistory.length > 0) {
      setLocalChatHistory(chatHistory);
    } else {
      setLocalChatHistory([]); // Initialize with empty array instead of undefined
    }
  }, [chatHistory]);

  const [activeCard, setActiveCard] = useState(null);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState("source"); // 'source', 'chat', 'studio'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1440);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1440;
      setIsMobile(mobile);

      // If switching to mobile and no tab is active, set default to 'source'
      if (mobile && !activeMobileTab) {
        setActiveMobileTab("source");
      }

      // If switching to desktop, reset collapse states if needed
      if (!mobile) {
        // Keep current collapse states, just ensure we're not in mobile mode
      }
    };

    window.addEventListener("resize", handleResize);

    // Initial check
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeMobileTab]);
  const [chatInput, setChatInput] = useState("");
  const [editingItem, setEditingItem] = useState(null); // {type, id}
  const [newTitle, setNewTitle] = useState("");
  const [menuItem, setMenuItem] = useState(null); // {type, id}
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [currentFlashcardSet, setCurrentFlashcardSet] = useState(null);
  const [currentFlashcards, setCurrentFlashcards] = useState([]);
  const [currentFlashcardSetId, setCurrentFlashcardSetId] = useState(null);
  const [currentAudioSummary, setCurrentAudioSummary] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentMindMap, setCurrentMindMap] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [localChatHistory, setLocalChatHistory] = useState([]);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isGeneratingMindMap, setIsGeneratingMindMap] = useState(false);
  const [isGeneratingFlashcards, setIsGeneratingFlashcards] = useState(false);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [showNoSourceToast, setShowNoSourceToast] = useState(false);

  // Reset function to clean up state after generation
  const resetGenerationState = useCallback(() => {
    setIsGeneratingAudio(false);
    setIsGeneratingMindMap(false);
    setIsGeneratingFlashcards(false);
    setIsGeneratingQuiz(false);
  }, []);

  // Check if there are source documents available
  const hasSourceDocuments = documents && documents.length > 0;

  // Check if any AI generation is in progress
  const isAnyGenerating = isGeneratingAudio || isGeneratingMindMap || isGeneratingFlashcards || isGeneratingQuiz;

  // Show toast when no source documents
  const showNoSourceToastMessage = useCallback(() => {
    setShowNoSourceToast(true);
    setTimeout(() => setShowNoSourceToast(false), 5000); // Hide after 5 seconds
  }, []);

  const toggleLeftCollapsed = useCallback(
    debounce(() => setLeftCollapsed((prev) => !prev), 300),
    []
  );

  const toggleRightCollapsed = useCallback(
    debounce(() => {
      setRightCollapsed((prev) => !prev);
      // Reset active card when collapsing to prevent design issues
      if (!rightCollapsed) {
        setActiveCard(null);
      }
    }, 300),
    [rightCollapsed]
  );

  const noteCards = [
    { id: "flashcards", label: "Figma Flashcards", time: "12m", source: "1" },
    {
      id: "mindmap",
      label: "Figma Masterclass Brief",
      time: "12m",
      source: "1",
    },
    { id: "quiz", label: "Brief Quiz", time: "2d", source: "1" },
    {
      id: "audio",
      label: "Market Niche Masterclass: How one",
      time: "12m",
      source: "1",
    },
  ];

  const noteCardsIcon = {
    audioSummaries: <AudioIcon />,
    quizzes: <QuizIcon />,
    flashcardSets: <FlashcardsIcon />,
    mindMaps: <MindmapIcon />,
    notes: <TextFileIcon />,
  };

  const timeAgo = (dateString) => {
    if (!dateString) return "";
    const diffMs = Date.now() - new Date(dateString).getTime();
    const s = Math.floor(diffMs / 1000);
    if (s < 60) return `${s}s Ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m Ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h Ago`;
    const d = Math.floor(h / 24);
    return `${d}d Ago`;
  };

  const sourceData = (documents || []).map((doc, idx) => ({
    id: doc.filename || `doc-${idx}`,
    label: doc.name,
    type: doc.type,
  }));

  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const CardActive = (id) => {
    setActiveCard(id);
    setRightCollapsed(false);
  };

  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState("");

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection("next");
    setTimeout(() => {
      const newCards = [...currentFlashcards];
      const topCard = newCards.shift();
      newCards.push({ ...topCard, flipped: false });
      setCurrentFlashcards(newCards);
      setIsAnimating(false);
    }, 600);
  };

  const handleBack = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection("back");
    setTimeout(() => {
      const newCards = [...currentFlashcards];
      const lastCard = newCards.shift();
      newCards.push({ ...lastCard, flipped: false });
      setCurrentFlashcards(newCards);
      setIsAnimating(false);
    }, 600);
  };

  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const chatContainerRef = useRef(null);

  const toggleFlip = (id) => {
    setCurrentFlashcards((prev) =>
      prev.map((card) => {
        if (card.id === id) {
          const newFlipped = !card.flipped;
          if (newFlipped && !card.isReviewed) {
            dispatch(reviewFlashcard({ token, courseId, flashcardId: id }));
          }
          return { ...card, flipped: newFlipped };
        }
        return card;
      })
    );
  };

  const handleMenuClick = (type, id) => {
    setMenuItem(
      menuItem && menuItem.type === type && menuItem.id === id
        ? null
        : { type, id }
    );
  };

  const handleEditClick = (type, item) => {
    setEditingItem({ type, id: item.id });
    setNewTitle(item.title || "");
    setMenuItem(null);
  };

  const handleShareClick = (type, id) => {
    // TODO: Implement share later
    console.log(`Share ${type} ${id}`);
    setMenuItem(null);
  };

  const handleDeleteClick = (type, id) => {
    // TODO: Implement delete later
    console.log(`Delete ${type} ${id}`);
    setMenuItem(null);
  };

  const handleCancel = () => {
    setEditingItem(null);
    setNewTitle("");
  };

  const handleSave = async (type, id) => {
    if (!newTitle.trim()) return;
    let updateAction;
    let idKey;
    switch (type) {
      case "notes":
        updateAction = updateNoteTitle;
        idKey = "noteId";
        break;
      case "flashcardSets":
        updateAction = updateFlashcardSetTitle;
        idKey = "flashcardSetId";
        break;
      case "audioSummaries":
        updateAction = updateAudioSummaryTitle;
        idKey = "audioSummaryId";
        break;
      case "quizzes":
        updateAction = updateQuizTitle;
        idKey = "quizId";
        break;
      case "mindMaps":
        updateAction = updateMindMapTitle;
        idKey = "mindMapId";
        break;
      default:
        return;
    }
    try {
      await dispatch(
        updateAction({ token, courseId, [idKey]: id, title: newTitle })
      ).unwrap();
      // Removed redundant fetchStudentNotebook call - not needed for title updates
      setEditingItem(null);
      setNewTitle("");
    } catch (err) {
      console.error("Update failed:", err);
      // Optionally show error to user
    }
  };

  const handleOpenNote = async (noteId) => {
    try {
      const response = await dispatch(
        fetchNote({ token, courseId, noteId })
      ).unwrap();
      const note = response?.data || response; // Adjust based on response structure
      setNoteTitle(note.title || "");
      setNoteContent(note.content || "");
      setCurrentNoteId(noteId);
      setActiveCard("note");
    } catch (err) {
      console.error("Fetch note failed:", err);
    }
  };

  const handleAddNote = () => {
    if (isAnyGenerating) return;
    // Check if there are source documents
    if (!hasSourceDocuments) {
      showNoSourceToastMessage();
      return;
    }

    setNoteTitle("");
    setNoteContent("");
    setCurrentNoteId(null);
    setActiveCard("note");
  };

  const handleSaveNote = async () => {
    if (!noteTitle.trim()) return;
    try {
      if (currentNoteId) {
        await dispatch(
          updateNote({
            token,
            courseId,
            noteId: currentNoteId,
            title: noteTitle,
            content: noteContent,
          })
        ).unwrap();
      } else {
        await dispatch(
          createNote({
            token,
            courseId,
            title: noteTitle,
            content: noteContent,
          })
        ).unwrap();
      }
      // Removed redundant fetchStudentNotebook call - Redux state is already updated
      setActiveCard(null);
      setNoteTitle("");
      setNoteContent("");
      setCurrentNoteId(null);
    } catch (err) {
      console.error("Save note failed:", err);
      // Optionally show error to user
    }
  };

  const handleOpenFlashcardSet = async (setId) => {
    try {
      const response = await dispatch(
        fetchFlashcards({ token, courseId, flashcardSetId: setId })
      ).unwrap();
      setCurrentFlashcardSet(response.data.flashcardSet);
      setCurrentFlashcards(
        response.data.flashcards.map((card) => ({ ...card, flipped: false }))
      );
      setCurrentFlashcardSetId(setId);
      setActiveCard("flashcards");
    } catch (err) {
      console.error("Fetch flashcards failed:", err);
    }
  };

  const handleOpenAudioSummary = async (summaryId) => {
    try {
      const response = await dispatch(
        fetchAudioSummary({ token, courseId, summaryId })
      ).unwrap();
      setCurrentAudioSummary(response.data);
      setActiveCard("audio");
    } catch (err) {
      console.error("Fetch audio summary failed:", err);
    }
  };

  const handleOpenQuiz = async (quizId) => {
    console.log(
      "handleOpenQuiz called with quizId:",
      quizId,
      "courseId:",
      courseId
    ); // Debug log
    try {
      const response = await dispatch(
        fetchQuiz({ token, courseId, quizId })
      ).unwrap();
      console.log("Quiz response in handleOpenQuiz:", response); // Debug log
      setCurrentQuiz(response.data.quiz); // Use the correct path to quiz data
      setActiveCard("quiz");
    } catch (err) {
      console.error("Fetch quiz failed:", err);
    }
  };

  const handleOpenMindMap = async (mindMapId) => {
    try {
      const response = await dispatch(
        fetchMindMap({ token, courseId, mindMapId })
      ).unwrap();
      console.log("MindMap response:", response);
      setCurrentMindMap(response.data);
      setActiveCard("mindmap");
    } catch (err) {
      console.error("Fetch mind map failed:", err);
    }
  };

  // Handle individual document selection
  const handleDocumentSelect = (docId) => {
    setSelectedDocuments((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(docId)) {
        newSelected.delete(docId);
      } else {
        newSelected.add(docId);
      }
      return newSelected;
    });
  };

  // Handle select all functionality
  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedDocuments(new Set());
      setSelectAll(false);
    } else {
      // Select all
      const allDocIds = sourceData.map((doc) => doc.id);
      setSelectedDocuments(new Set(allDocIds));
      setSelectAll(true);
    }
  };

  // Update select all state when individual selections change
  useEffect(() => {
    const totalDocs = sourceData.length;
    const selectedCount = selectedDocuments.size;

    if (selectedCount === 0) {
      setSelectAll(false);
    } else if (selectedCount === totalDocs) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedDocuments, sourceData.length]);

  // Handle audio summary generation
  const handleGenerateAudioSummary = async () => {
    if (isAnyGenerating) return;

    // Check if there are source documents
    if (!hasSourceDocuments) {
      showNoSourceToastMessage();
      return;
    }

    setIsGeneratingAudio(true);

    try {
      // Convert selected documents Set to Array, or send null if none selected
      const selectedDocsArray =
        selectedDocuments.size > 0 ? Array.from(selectedDocuments) : null;

      console.log(
        "Generating audio summary with selected documents:",
        selectedDocsArray
      );

      const result = await dispatch(
        generateAudioSummary({
          token,
          courseId,
          voiceStyle: "educational",
          language: "en",
          selectedDocuments: selectedDocsArray,
        })
      ).unwrap();

      console.log("Audio summary generation result:", result);

      // The Redux state should already be updated by the fulfilled case
      // No need to fetch again as it's already added to the state
    } catch (err) {
      console.error("Generate audio summary failed:", err);
      // Optionally show error to user
    } finally {
      setIsGeneratingAudio(false);
      // Reset all generation states to prevent UI issues
      resetGenerationState();
    }
  };

  // Handle mind map generation
  const handleGenerateMindMap = async () => {
    if (isAnyGenerating) return;

    // Check if there are source documents
    if (!hasSourceDocuments) {
      showNoSourceToastMessage();
      return;
    }

    setIsGeneratingMindMap(true);

    try {
      // Convert selected documents Set to Array, or send null if none selected
      const selectedDocsArray =
        selectedDocuments.size > 0 ? Array.from(selectedDocuments) : null;

      console.log(
        "Generating mind map with selected documents:",
        selectedDocsArray
      );

      const result = await dispatch(
        generateMindMap({
          token,
          courseId,
          sourceDocuments: selectedDocsArray || [],
          title: "AI Generated Mind Map",
          difficulty: "medium",
          category: "general",
          selectedDocuments: selectedDocsArray,
        })
      ).unwrap();

      console.log("Mind map generation result:", result);

      // The Redux state should already be updated by the fulfilled case
      // No need to fetch again as it's already added to the state
    } catch (err) {
      console.error("Generate mind map failed:", err);
      // Optionally show error to user
    } finally {
      setIsGeneratingMindMap(false);
      // Reset all generation states to prevent UI issues
      resetGenerationState();
    }
  };

  // Handle flashcard generation
  const handleGenerateFlashcards = async () => {
    if (isAnyGenerating) return;

    // Check if there are source documents
    if (!hasSourceDocuments) {
      showNoSourceToastMessage();
      return;
    }

    setIsGeneratingFlashcards(true);

    try {
      // Convert selected documents Set to Array, or send null if none selected
      const selectedDocsArray =
        selectedDocuments.size > 0 ? Array.from(selectedDocuments) : null;

      console.log(
        "Generating flashcards with selected documents:",
        selectedDocsArray
      );

      const result = await dispatch(
        generateFlashcards({
          token,
          courseId,
          sourceDocuments: selectedDocsArray || [],
          count: 5,
          difficulty: "medium",
          category: "selected-docs",
          selectedDocuments: selectedDocsArray,
        })
      ).unwrap();

      console.log("Flashcard generation result:", result);

      // The Redux state should already be updated by the fulfilled case
      // No need to fetch again as it's already added to the state
    } catch (err) {
      console.error("Generate flashcards failed:", err);
      // Optionally show error to user
    } finally {
      setIsGeneratingFlashcards(false);
      // Reset all generation states to prevent UI issues
      resetGenerationState();
    }
  };

  // Handle quiz generation
  const handleGenerateQuiz = async () => {
    if (isAnyGenerating) return;

    // Check if there are source documents
    if (!hasSourceDocuments) {
      showNoSourceToastMessage();
      return;
    }

    setIsGeneratingQuiz(true);

    try {
      // Convert selected documents Set to Array, or send null if none selected
      const selectedDocsArray =
        selectedDocuments.size > 0 ? Array.from(selectedDocuments) : null;

      console.log(
        "Generating quiz with selected documents:",
        selectedDocsArray
      );

      const result = await dispatch(
        generateQuiz({
          token,
          courseId,
          sourceDocuments: selectedDocsArray || [],
          difficulty: "medium",
          timeLimit: 30,
          title: "AI Generated Quiz",
          selectedDocuments: selectedDocsArray,
        })
      ).unwrap();

      console.log("Quiz generation result:", result);

      // The Redux state should already be updated by the fulfilled case
      // No need to fetch again as it's already added to the state
    } catch (err) {
      console.error("Generate quiz failed:", err);
      // Optionally show error to user
    } finally {
      setIsGeneratingQuiz(false);
      // Reset all generation states to prevent UI issues
      resetGenerationState();
    }
  };

  // Handle sending chat message with selected documents
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isSendingMessage) return;

    const userMessage = {
      id: Date.now(), // Temporary ID
      type: "user",
      message: chatInput.trim(),
      timestamp: new Date().toISOString(),
    };

    // Add user message immediately to local chat history
    setLocalChatHistory((prev) => [...(prev || []), userMessage]);

    // Clear input immediately
    setChatInput("");

    // Show typing indicator
    setShowTypingIndicator(true);
    setIsSendingMessage(true);

    // Auto-scroll to bottom
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 100);

    try {
      console.log("Selected documents:", selectedDocuments);
      // Convert selected documents Set to Array, or send null if none selected
      const selectedDocsArray =
        selectedDocuments.size > 0 ? Array.from(selectedDocuments) : null;

      console.log(
        "Sending chat message with selected documents:",
        selectedDocsArray
      );

      // Send message with selected documents
      const response = await dispatch(
        sendChatMessage({
          token,
          courseId,
          message: userMessage.message,
          selectedDocuments: selectedDocsArray,
        })
      ).unwrap();

      // Hide typing indicator
      setShowTypingIndicator(false);

      // Add assistant response to local chat history
      if (response.data && response.data.assistantResponse) {
        setLocalChatHistory((prev) => [
          ...(prev || []),
          response.data.assistantResponse,
        ]);

        // Auto-scroll to bottom after response
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (err) {
      console.error("Send message failed:", err);
      setShowTypingIndicator(false);
      // Ensure localChatHistory is always an array
      setLocalChatHistory((prev) => prev || []);
      // Optionally show error to user
    } finally {
      setIsSendingMessage(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".dots-btn") &&
        !event.target.closest(".custom-menu")
      ) {
        setMenuItem(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [localChatHistory, showTypingIndicator]);

  // Handle collapse state changes to prevent UI issues
  useEffect(() => {
    if (rightCollapsed) {
      // When collapsing, reset active card and generation states
      setActiveCard(null);
      resetGenerationState();
    }
  }, [rightCollapsed, resetGenerationState]);

  const renderItemCard = (item, type) => {
    const isEditing =
      editingItem && editingItem.type === type && editingItem.id === item.id;
    const isMenuOpen =
      menuItem && menuItem.type === type && menuItem.id === item.id;
    const handleClick = () => {
      if (type === "notes") {
        handleOpenNote(item.id);
      } else if (type === "flashcardSets") {
        handleOpenFlashcardSet(item.id);
      } else if (type === "audioSummaries") {
        handleOpenAudioSummary(item.id);
      } else if (type === "quizzes") {
        handleOpenQuiz(item.id);
      } else if (type === "mindMaps") {
        handleOpenMindMap(item.id);
      }
    };
    return (
      <div
        key={item.id}
        className="note-card p-10 mb-8 d-flex align-items-center justify-content-between position-relative"
      >
        <div
          className="d-flex align-items-center flex-grow-1"
          onClick={!isEditing ? handleClick : undefined}
          style={{ cursor: isEditing ? "default" : "pointer" }}
        >
          {isEditing ? (
            <div onClick={(e) => e.stopPropagation()} className="flex-grow-1">
              <input
                type="text"
                className="form-control"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Enter title..."
                style={{
                  width: "100%",
                  border: "2px solid #e8eaed",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#202124",
                  background: "#ffffff",
                  transition: "all 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  marginBottom: "8px",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#f05203";
                  e.target.style.boxShadow = "0 0 0 3px rgba(240, 82, 3, 0.15)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e8eaed";
                  e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                  e.target.style.transform = "translateY(0)";
                }}
              />
              <div className="d-flex gap-2" style={{ width: "100%" }}>
                <button
                  className="btn btn-secondary min-w-auto no-shadow flex-grow-1 py-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary min-w-auto no-shadow flex-grow-1 py-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSave(type, item.id);
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <span className="bg-white rounded-circle d-flex justify-content-center align-items-center note-icon me-15">
                {noteCardsIcon[type]}
              </span>
              <div className="d-flex flex-column">
                <span
                  className="ah-text-xs fw-semibold text-black-100 text-truncate"
                  style={{ maxWidth: "220px" }}
                >
                  {item.title ||
                    type.charAt(0).toUpperCase() + type.slice(1, -1)}
                </span>
                <span className="ah-text-xs text-text-black-100 fw-normal">
                  {timeAgo(item.createdAt)}
                </span>
              </div>
            </>
          )}
        </div>
        {!isEditing && (
          <div className="position-relative">
            <button
              className="dots-btn position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
              onClick={() => handleMenuClick(type, item.id)}
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: "none",
                background: isMenuOpen ? "#e8f0fe" : "transparent",
                color: isMenuOpen ? "#1a73e8" : "#5f6368",
                transition: "all 0.2s ease",
                cursor: "pointer",
                zIndex: 1,
              }}
              onMouseEnter={(e) => {
                if (!isMenuOpen) {
                  e.target.style.background = "#f8f9fa";
                  e.target.style.color = "#1a73e8";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMenuOpen) {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#5f6368";
                }
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <circle cx="8" cy="3" r="1.5" />
                <circle cx="8" cy="8" r="1.5" />
                <circle cx="8" cy="13" r="1.5" />
              </svg>
            </button>
            {isMenuOpen && (
              <div
                className="custom-menu position-absolute end-0"
                style={{
                  zIndex: 20,
                  top: "100%",
                  marginTop: "4px",
                  animation: "fadeInUp 0.2s ease-out",
                }}
              >
                <div
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    border: "1px solid #e8eaed",
                    padding: "8px",
                    minWidth: "120px",
                    overflow: "hidden",
                  }}
                >
                  <button
                    className="menu-item d-flex align-items-center gap-3 w-100 border-0 bg-transparent"
                    onClick={() => handleEditClick(type, item)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      color: "#1a73e8",
                      fontSize: "14px",
                      fontWeight: "500",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#e8f0fe";
                      e.target.style.transform = "translateX(2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.transform = "translateX(0)";
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Zm1.414 1.06a.25.25 0 0 0-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 0 0 0-.354L12.427 2.487ZM11.189 6.25 9.75 4.81l-6.286 6.287a.25.25 0 0 0-.064.108l-.558 1.953 1.953-.558a.25.25 0 0 0 .108-.064l6.286-6.286Z" />
                    </svg>
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          
          @keyframes fadeInUp {
            0% {
              opacity: 0;
              transform: translateY(10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          /* Custom scrollbar for combined chat section */
          .chat-box::-webkit-scrollbar {
            width: 6px;
          }
          
          .chat-box::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          .chat-box::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .chat-box::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          /* Firefox scrollbar styling */
          .chat-box {
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }
          
          /* Mobile responsive styles */
          @media (max-width: 1439.98px) {
            .mobile-tabs {
              position: sticky;
              top: 0;
              z-index: 1000;
              box-shadow: 0 0 16px 2px rgba(0,0,0,0.1);
            }
            
            .tab-btn {
              font-size: 14px;
              font-weight: 500;
            }
            
            .tab-btn:hover {
              background-color: #f8f9fa !important;
            }
            
            .tab-btn.active {
              background-color: rgba(240, 82, 3, 0.1) !important;
            }
            
            .grid-container {
              grid-template-columns: 1fr !important;
              gap: 0 !important;
            }
            
            .left, .center, .right {
              min-width: 100% !important;
              max-width: 100% !important;
            }
            
            /* Hide toggle buttons on mobile */
            .toggle-btn {
              display: none !important;
            }
            
            /* Fix Add Note button positioning on mobile */
            .studio-items {
              padding-bottom: 80px !important;
            }
            
            
          }
        `}
      </style>
      {/* Loading overlay - only for notebook fetch */}
      {isNotebookLoading ? (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      ) : (
        <div className="p-15 p-sm-25 min-vh-100 d-flex flex-column">
          {/* No Source Documents Toast */}
          {showNoSourceToast && (
            <div
              className="position-fixed top-0 start-50 translate-middle-x mt-4"
              style={{ zIndex: 10000 }}
            >
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <strong>No Source Documents!</strong> There are no source
                  documents in this course. Please add documents to enable AI
                  features.
                </div>
                <button
                  type="button"
                  className="btn-close ms-3"
                  onClick={() => setShowNoSourceToast(false)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          )}

          <div className="topbar mb-20 mb-lg-36">
            <div className="d-flex align-items-center justify-content-between">
              <h3
                className="ah-heading-04 fw-bold black-800 me-29 mb-0 d-flex align-items-center gap-3 cursor-pointer"
                onClick={goBack}
              >
                <span className="icon-arrow-left"></span>
                {notebook?.courseTitle || "Course Title"}
              </h3>
              <div className="search-wrapper d-none d-lg-flex align-items-center gap-3">
                <Notification />
              </div>
            </div>
          </div>
          {/* Mobile Tab Navigation - Only visible on small screens */}
          {isMobile && (
            <div
              className="mobile-tabs mb-16 d-flex bg-white rounded-4 p-8"
              style={{
                borderBottom: "1px solid #e8eaed",
              }}
            >
              <button
                className={`tab-btn flex-fill py-10 py-sm-16 px-10 px-sm-18 rounded-3 px-md-24 border-0 bg-transparent ${
                  activeMobileTab === "source" ? "active" : ""
                }`}
                onClick={() => setActiveMobileTab("source")}
                style={{
                  borderBottom:
                    activeMobileTab === "source"
                      ? "2px solid #f05203"
                      : "2px solid transparent !important",
                  color: activeMobileTab === "source" ? "#f05203" : "#5f6368",
                  fontWeight: activeMobileTab === "source" ? "600" : "400",
                  transition: "all 0.2s ease",
                }}
              >
                Source
              </button>
              <button
                className={`tab-btn flex-fill py-10 py-sm-16 px-10 px-sm-18 rounded-3 px-md-24 border-0 bg-transparent ${
                  activeMobileTab === "chat" ? "active" : ""
                }`}
                onClick={() => setActiveMobileTab("chat")}
                style={{
                  borderBottom:
                    activeMobileTab === "chat"
                      ? "2px solid #f05203"
                      : "2px solid transparent",
                  color: activeMobileTab === "chat" ? "#f05203" : "#5f6368",
                  fontWeight: activeMobileTab === "chat" ? "600" : "400",
                  transition: "all 0.2s ease",
                }}
              >
                Chat
              </button>
              <button
                className={`tab-btn flex-fill py-10 py-sm-16 px-10 px-sm-18 rounded-3 px-md-24 border-0 bg-transparent ${
                  activeMobileTab === "studio" ? "active" : ""
                }`}
                onClick={() => setActiveMobileTab("studio")}
                style={{
                  borderBottom:
                    activeMobileTab === "studio"
                      ? "2px solid #f05203"
                      : "2px solid transparent",
                  color: activeMobileTab === "studio" ? "#f05203" : "#5f6368",
                  fontWeight: activeMobileTab === "studio" ? "600" : "400",
                  transition: "all 0.2s ease",
                }}
              >
                Studio
              </button>
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          <div
            className={`grid-container p-0 p-sm-7 h-100 flex-grow-1 ${
              activeCard ? "card-view" : ""
            } ${leftCollapsed ? "left-collapsed" : ""} ${
              rightCollapsed ? "right-collapsed" : ""
            }`}
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr" // Single column on mobile
                : leftCollapsed && rightCollapsed
                ? "60px 1fr 60px"
                : leftCollapsed
                ? activeCard
                  ? "60px 1fr 600px" // Expand right panel when item is opened
                  : "60px 1fr 400px"
                : rightCollapsed
                ? "400px 1fr 60px"
                : activeCard
                ? "400px 1fr 600px" // Expand right panel when item is opened
                : "400px 1fr 400px",
              gap: isMobile ? "0" : "30px",
              height: "100%",
              transition: "grid-template-columns 0.3s ease",
            }}
          >
            {/* Left Column - Source */}
            <div
              className={`left ${
                isMobile
                  ? activeMobileTab === "source"
                    ? "d-block"
                    : "d-none"
                  : ""
              }`}
              style={{
                minWidth: isMobile ? "100%" : leftCollapsed ? "60px" : "400px",
                maxWidth: isMobile ? "100%" : leftCollapsed ? "60px" : "400px",
                overflow: "hidden",
                transition: "all 0.3s ease",
              }}
            >
              <div className="header mb-20">
                {!leftCollapsed ? <h3>Source</h3> : ""}
                <button
                  className="toggle-btn d-none d-lg-block"
                  onClick={toggleLeftCollapsed}
                  style={{ display: isMobile ? "none" : "block" }}
                >
                  <LeftToggleIcon />
                </button>
              </div>
              <div className="source-wrapper">
                {!leftCollapsed ? (
                  <>
                    {/* Select All Button - Only show if 2 or more documents */}
                    {sourceData.length >= 2 && (
                      <div className="my-15">
                        <label
                          htmlFor="selectAll"
                          className="files-radio p-15 d-flex justify-content-between align-items-center mb-12"
                        >
                          <div className="d-flex">
                            <span className="fw-semibold ah-text-xs">
                              Select All ({sourceData.length} documents)
                            </span>
                          </div>
                          <input
                            checked={selectAll}
                            onChange={handleSelectAll}
                            type="checkbox"
                            id="selectAll"
                            className="flex-shrink-0"
                          />
                        </label>
                      </div>
                    )}

                    <div className="my-20">
                      {sourceData.map((item, index) => (
                        <label
                          htmlFor={item?.id}
                          className="files-radio p-15 d-flex justify-content-between align-items-center mb-12"
                          key={index}
                        >
                          <div className="d-flex">
                            {item?.type === "application/pdf" ? (
                              <PdfIcon />
                            ) : (
                              <DocIcon />
                            )}
                            <span className="ms-11 fw-semibold ah-text-xs">
                              {item?.label}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            id={item?.id}
                            checked={selectedDocuments.has(item.id)}
                            onChange={() => handleDocumentSelect(item.id)}
                            className="flex-shrink-0"
                          />
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Select All Button for collapsed view */}
                    {sourceData.length >= 2 && (
                      <div className="my-15">
                        <button
                          className="select-all-btn-collapsed p-8 d-flex align-items-center justify-content-center w-100"
                          onClick={handleSelectAll}
                          style={{
                            background: selectAll ? "#f05203" : "#f8f9fa",
                            border: `1px solid ${
                              selectAll ? "#f05203" : "#dadce0"
                            }`,
                            borderRadius: "6px",
                            transition: "all 0.2s ease",
                            color: selectAll ? "white" : "#5f6368",
                            fontSize: "10px",
                            fontWeight: "500",
                          }}
                        >
                          {selectAll ? "✓ All" : "Select All"}
                        </button>
                      </div>
                    )}

                    <div className="my-20">
                      {sourceData.map((item, index) => (
                        <label
                          htmlFor={item?.id}
                          className="files-radio p-10 d-flex justify-content-between align-items-center mb-12"
                          key={index}
                          // style={{
                          //   background: selectedDocuments.has(item.id) ? '#e8f0fe' : 'transparent',
                          //   border: `1px solid ${selectedDocuments.has(item.id) ? '#1a73e8' : 'transparent'}`,
                          //   borderRadius: '6px',
                          //   transition: 'all 0.2s ease'
                          // }}
                        >
                          <div className="d-flex">
                            {item?.type === "application/pdf" ? (
                              <PdfIcon />
                            ) : (
                              <DocIcon />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            id={item?.id}
                            checked={selectedDocuments.has(item.id)}
                            onChange={() => handleDocumentSelect(item.id)}
                            hidden
                          />
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Center Column - Chat */}
            <div
              className={`center ${
                isMobile
                  ? activeMobileTab === "chat"
                    ? "d-flex"
                    : "d-none"
                  : ""
              }`}
              style={{
                minWidth: isMobile ? "100%" : "0",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="header mb-20">
                <h3>Chat</h3>
              </div>
              <div
                className="chat-section py-15 px-8"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                {/* Combined Scrollable Container for Title, Description, and Chat History */}
                <div
                  ref={chatContainerRef}
                  className="chat-box"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "10px",
                    marginBottom: "20px",
                  }}
                >
                  {/* Course Title and Description - Now part of scrollable content */}
                  <div className="mb-20">
                    <h2 className="ah-heading-02 fw-bold text-black-100 mb-15">
                      {notebook?.courseTitle || "Course Title"}
                    </h2>
                    <span className="theme-color ah-text-xs fw-medium">
                      {selectedDocuments.size > 0
                        ? `${selectedDocuments.size} Source${
                            selectedDocuments.size > 1 ? "s" : ""
                          }`
                        : `${documents?.length || 0} Source${
                            (documents?.length || 0) > 1 ? "s" : ""
                          }`}
                    </span>
                    <p className="ah-text-base fw-normal text-gray-01 mt-15 mb-15 mb-sm-32">
                      {(() => {
                        if (!documents || documents.length === 0) {
                          return "Summary not available for this course yet.";
                        }

                        // If documents are selected, show summary from selected documents only
                        if (selectedDocuments.size > 0) {
                          const selectedDocs = documents.filter((doc) =>
                            selectedDocuments.has(doc.filename)
                          );
                          console.log(
                            "Selected documents for summary:",
                            selectedDocs.map((d) => d.name)
                          );

                          if (selectedDocs.length === 0) {
                            return "No summary available for selected documents.";
                          }

                          // Combine summaries from selected documents
                          const summaries = selectedDocs
                            .map((doc) => doc.summary)
                            .filter(
                              (summary) => summary && summary.trim().length > 0
                            );

                          if (summaries.length === 0) {
                            return "No summary available for selected documents.";
                          }

                          if (summaries.length === 1) {
                            return summaries[0];
                          }

                          // Multiple summaries - combine them with document names
                          return summaries
                            .map(
                              (summary, index) =>
                                `${selectedDocs[index].name}: ${summary}`
                            )
                            .join("\n\n");
                        } else {
                          // No documents selected, show summary from all documents
                          console.log(
                            "No documents selected, showing all documents summary"
                          );
                          const summaries = documents
                            .map((doc) => doc.summary)
                            .filter(
                              (summary) => summary && summary.trim().length > 0
                            );

                          if (summaries.length === 0) {
                            return "Summary not available for this course yet.";
                          }

                          if (summaries.length === 1) {
                            return summaries[0];
                          }

                          // Multiple summaries - combine them with document names
                          return summaries
                            .map(
                              (summary, index) =>
                                `${documents[index].name}: ${summary}`
                            )
                            .join("\n\n");
                        }
                      })()}
                    </p>
                  </div>

                  {/* Chat History - Now part of the same scrollable container */}
                  {(localChatHistory || []).length === 0 ? (
                    <div className="text-center py-4">
                      <div className="ah-text-xs text-muted">
                        No messages yet. Start a conversation!
                      </div>
                    </div>
                  ) : (
                    (localChatHistory || []).map((msg) =>
                      msg.type === "assistant" ? (
                        <div
                          key={msg.id}
                          className="message bot-message"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "20px",
                            gap: "12px",
                          }}
                        >
                          <div
                            className="bot-icon"
                            style={{
                              flexShrink: 0,
                              width: "32px",
                              height: "32px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <BotIcon />
                          </div>
                          <div
                            className="bot-bubble d-flex flex-column text-start"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              maxWidth: "100%",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            <ReactMarkdown>{msg.message}</ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={msg.id}
                          className="message user-message"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginBottom: "20px",
                            gap: "12px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <img
                            className="user-img"
                            src={
                              user?.profileImage?.startsWith("/")
                                ? process.env.REACT_APP_GALLERY_URL +
                                  "/" +
                                  user.profileImage
                                : user?.profileImage
                                ? process.env.REACT_APP_GALLERY_URL +
                                  user.profileImage
                                : userImg
                            }
                            alt={user?.fullName || "User"}
                            style={{
                              flexShrink: 0,
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = userImg;
                            }}
                          />
                          <div
                            className="user-bubble d-flex flex-column text-start"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              maxWidth: "100%",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              textAlign: "right",
                            }}
                          >
                            <span>You</span>
                            <p>{msg.message}</p>
                          </div>
                          {console.log("image", user?.profileImage)}
                          {console.log(
                            "test",
                            user?.profileImage?.startsWith("/")
                          )}
                          {console.log(
                            "test2",
                            process.env.REACT_APP_GALLERY_URL +
                              "/" +
                              user.profileImage
                          )}
                          {console.log(
                            "test3",
                            process.env.REACT_APP_GALLERY_URL +
                              user.profileImage
                          )}
                          {console.log("test4", userImg)}
                        </div>
                      )
                    )
                  )}

                  {/* Bouncing dots typing indicator */}
                  {showTypingIndicator && (
                    <div className="dottedLoading theme-color"></div>
                  )}
                </div>
              </div>
              <div className="chat-bottom mt-14" style={{ flexShrink: 0 }}>
                <div className="card-default-style py-8 px-13">
                  <div className="d-flex gap-3 align-items-center mb-10">
                    <input
                      type="text"
                      className="w-100 border-0 focus-ring focus-ring-light px-10 py-5 bg-transparent flex-1"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          !isSendingMessage
                        ) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Ask about the course..."
                      disabled={isSendingMessage}
                      style={{
                        minWidth: 0,
                        flex: 1,
                        opacity: isSendingMessage ? 0.6 : 1,
                      }}
                    />
                    <span className="theme-color ah-text-xs fw-medium flex-shrink-0">
                      {selectedDocuments.size > 0
                        ? `${selectedDocuments.size} Source${
                            selectedDocuments.size > 1 ? "s" : ""
                          }`
                        : `${documents?.length || 0} Source${
                            (documents?.length || 0) > 1 ? "s" : ""
                          }`}
                    </span>
                    <button
                      className="bg-theme border-0 rounded-circle text-white flex-shrink-0 d-flex align-items-center justify-content-center p-0"
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || isSendingMessage}
                      style={{
                        opacity:
                          !chatInput.trim() || isSendingMessage ? 0.6 : 1,
                        width: "35px",
                        height: "35px",
                        cursor:
                          !chatInput.trim() || isSendingMessage
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      <SendIcon />
                    </button>
                  </div>
                  <div
                    className="suggestions-wrapper d-flex align-items-center flex-nowrap gap-3 overflow-x-auto py-5"
                    style={{
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {(() => {
                      // Get questions from selected documents only, or all documents if none selected
                      const docsToProcess =
                        selectedDocuments.size > 0
                          ? documents.filter((doc) =>
                              selectedDocuments.has(doc.filename)
                            )
                          : documents;

                      console.log(
                        "Processing questions for documents:",
                        docsToProcess.map((d) => d.name)
                      );
                      console.log(
                        "Selected documents:",
                        Array.from(selectedDocuments)
                      );

                      let allSuggestions = [];
                      docsToProcess.forEach((doc) => {
                        const raw = doc?.suggestedQuestions;
                        let suggestions = [];
                        try {
                          if (Array.isArray(raw)) {
                            suggestions = raw;
                          } else if (typeof raw === "string") {
                            let parsed = null;
                            try {
                              parsed = JSON.parse(raw);
                            } catch (_) {}
                            if (Array.isArray(parsed)) {
                              suggestions = parsed;
                            } else {
                              suggestions = raw
                                .split(/\r?\n|,/)
                                .map((s) => s.trim())
                                .filter((s) => s.length > 0);
                            }
                          }
                        } catch (_) {
                          suggestions = [];
                        }

                        // Add document context to questions if multiple documents are being processed
                        if (docsToProcess.length > 1) {
                          suggestions = suggestions.map(
                            (q) => `[${doc.name}] ${q}`
                          );
                        }

                        allSuggestions = [...allSuggestions, ...suggestions];
                      });

                      // Remove duplicates and limit to 6 questions
                      const uniqueSuggestions = [...new Set(allSuggestions)]
                        .map((s) =>
                          typeof s === "string" ? s : String(s ?? "")
                        )
                        .filter((s) => s.length > 0)
                        .slice(0, 6);

                      console.log(
                        "Combined questions from all documents:",
                        uniqueSuggestions
                      );
                      return uniqueSuggestions.length > 0 ? (
                        uniqueSuggestions.map((q, idx) => (
                          <span
                            key={idx}
                            className="suggestion theme-color ah-text-xs fw-normal py-5 px-10"
                            onClick={() => {
                              if (!isSendingMessage) {
                                setChatInput(q);
                                // Auto-send the suggestion
                                setTimeout(() => {
                                  handleSendMessage();
                                }, 100);
                              }
                            }}
                            style={{
                              cursor: isSendingMessage
                                ? "not-allowed"
                                : "pointer",
                              opacity: isSendingMessage ? 0.6 : 1,
                            }}
                          >
                            {q}
                          </span>
                        ))
                      ) : (
                        <span className="ah-text-xs text-gray-01">
                          No suggested questions yet.
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Studio */}
            <div
              className={`right position-relative h-100 ${
                isMobile
                  ? activeMobileTab === "studio"
                    ? "d-block"
                    : "d-none"
                  : ""
              }`}
              style={{
                minWidth: isMobile
                  ? "100%"
                  : rightCollapsed
                  ? "60px"
                  : activeCard
                  ? "600px"
                  : "400px",
                maxWidth: isMobile
                  ? "100%"
                  : rightCollapsed
                  ? "60px"
                  : activeCard
                  ? "600px"
                  : "400px",
                transition: "all 0.3s ease",
              }}
            >
              <div className="header mb-20">
                {!rightCollapsed ? (
                  <h3>
                    Studio{" "}
                    {activeCard === "flashcards" ? (
                      <span>
                        {" "}
                        <ChevronRight color="#000" /> Flashcards
                      </span>
                    ) : activeCard === "mindmap" ? (
                      <span>
                        <ChevronRight color="#000" /> Mind Map
                      </span>
                    ) : activeCard === "quiz" ? (
                      <span>
                        <ChevronRight color="#000" /> Quiz
                      </span>
                    ) : activeCard === "audio" ? (
                      <span>
                        <ChevronRight color="#000" /> Audio Notes
                      </span>
                    ) : activeCard === "note" ? (
                      <span>
                        <ChevronRight color="#000" /> Note
                      </span>
                    ) : (
                      ""
                    )}
                  </h3>
                ) : (
                  ""
                )}
                {!activeCard ? (
                  <button
                    className="toggle-btn d-none d-lg-block"
                    onClick={() => setRightCollapsed(!rightCollapsed)}
                    style={{ display: isMobile ? "none" : "block" }}
                  >
                    <RightToggleIcon />
                  </button>
                ) : (
                  <button
                    className="toggle-btn"
                    onClick={() => setActiveCard(null)}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
              {!activeCard ? (
                !rightCollapsed ? (
                  <div className="studio-items">
                    <div className="">
                      <div className="row g-3">
                        <div className="col-12 col-lg-6">
                          <div
                            className={`py-12 px-12 px-sm-21 rounded-4 custom-tooltip h-100 ${
                              hasSourceDocuments ? "bg-theme" : "bg-secondary"
                            }`}
                            data-title={
                              hasSourceDocuments
                                ? "Generate an AI podcast based on your sources"
                                : "No source documents available"
                            }
                            onClick={
                              hasSourceDocuments
                                ? handleGenerateAudioSummary
                                : showNoSourceToastMessage
                            }
                            style={{
                              cursor:
                                hasSourceDocuments && !isAnyGenerating
                                  ? "pointer"
                                  : "not-allowed",
                              opacity: hasSourceDocuments
                                ? isAnyGenerating
                                  ? 0.7
                                  : 1
                                : 0.6,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            {isGeneratingAudio ? (
                              <div className="d-flex flex-column align-items-center">
                                <div
                                  className="spinner-border spinner-border-sm text-white mb-2"
                                  role="status"
                                  style={{ width: "20px", height: "20px" }}
                                >
                                  <span className="visually-hidden">
                                    Generating...
                                  </span>
                                </div>
                                <span className="fw-medium text-white ah-test-xs">
                                  Generating...
                                </span>
                              </div>
                            ) : (
                              <>
                                <AudioOverviewIcon />
                                <span className="fw-medium text-white ah-test-xs d-block pt-10">
                                  Audio Overview
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div
                            className={`py-12 px-12 px-sm-21 rounded-4 custom-tooltip h-100 ${
                              hasSourceDocuments ? "bg-theme" : "bg-secondary"
                            }`}
                            data-title={
                              hasSourceDocuments
                                ? "Generate an AI mind map based on your sources"
                                : "No source documents available"
                            }
                            onClick={
                              hasSourceDocuments
                                ? handleGenerateMindMap
                                : showNoSourceToastMessage
                            }
                            style={{
                              cursor:
                                hasSourceDocuments && !isAnyGenerating
                                  ? "pointer"
                                  : "not-allowed",
                              opacity: hasSourceDocuments
                                ? isAnyGenerating
                                  ? 0.7
                                  : 1
                                : 0.6,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            {isGeneratingMindMap ? (
                              <div className="d-flex flex-column align-items-center">
                                <div
                                  className="spinner-border spinner-border-sm text-white mb-2"
                                  role="status"
                                  style={{ width: "20px", height: "20px" }}
                                >
                                  <span className="visually-hidden">
                                    Generating...
                                  </span>
                                </div>
                                <span className="fw-medium text-white ah-test-xs">
                                  Generating...
                                </span>
                              </div>
                            ) : (
                              <>
                                <MindmapOverviewIcon />
                                <span className="fw-medium text-white ah-test-xs d-block pt-10">
                                  Mind Map
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div
                            className={`py-12 px-12 px-sm-21 rounded-4 custom-tooltip h-100 ${
                              hasSourceDocuments ? "bg-theme" : "bg-secondary"
                            }`}
                            data-title={
                              hasSourceDocuments
                                ? "Generate AI flashcards based on your sources"
                                : "No source documents available"
                            }
                            onClick={
                              hasSourceDocuments
                                ? handleGenerateFlashcards
                                : showNoSourceToastMessage
                            }
                            style={{
                              cursor:
                                hasSourceDocuments && !isAnyGenerating
                                  ? "pointer"
                                  : "not-allowed",
                              opacity: hasSourceDocuments
                                ? isAnyGenerating
                                  ? 0.7
                                  : 1
                                : 0.6,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            {isGeneratingFlashcards ? (
                              <div className="d-flex flex-column align-items-center">
                                <div
                                  className="spinner-border spinner-border-sm text-white mb-2"
                                  role="status"
                                  style={{ width: "20px", height: "20px" }}
                                >
                                  <span className="visually-hidden">
                                    Generating...
                                  </span>
                                </div>
                                <span className="fw-medium text-white ah-test-xs">
                                  Generating...
                                </span>
                              </div>
                            ) : (
                              <>
                                <FlashcardsOverviewIcon />
                                <span className="fw-medium text-white ah-test-xs d-block pt-10">
                                  Flashcards
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div
                            className={`py-12 px-12 px-sm-21 rounded-4 custom-tooltip h-100 ${
                              hasSourceDocuments ? "bg-theme" : "bg-secondary"
                            }`}
                            data-title={
                              hasSourceDocuments
                                ? "Generate an AI interactive quiz based on your sources"
                                : "No source documents available"
                            }
                            onClick={
                              hasSourceDocuments
                                ? handleGenerateQuiz
                                : showNoSourceToastMessage
                            }
                            style={{
                              cursor:
                                hasSourceDocuments && !isAnyGenerating
                                  ? "pointer"
                                  : "not-allowed",
                              opacity: hasSourceDocuments
                                ? isAnyGenerating
                                  ? 0.7
                                  : 1
                                : 0.6,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            {isGeneratingQuiz ? (
                              <div className="d-flex flex-column align-items-center">
                                <div
                                  className="spinner-border spinner-border-sm text-white mb-2"
                                  role="status"
                                  style={{ width: "20px", height: "20px" }}
                                >
                                  <span className="visually-hidden">
                                    Generating...
                                  </span>
                                </div>
                                <span className="fw-medium text-white ah-test-xs">
                                  Generating...
                                </span>
                              </div>
                            ) : (
                              <>
                                <QuizOverviewIcon />
                                <span className="fw-medium text-white ah-test-xs d-block pt-10">
                                  Quiz
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <hr />
                      <div
                        className="list-scroll"
                        // style={{
                        //   maxHeight: isMobile ? "300px" : "450px",
                        //   overflowY: "auto",
                        //   overflowX: "hidden",
                        //   paddingBottom: isMobile ? "60px" : "0px",
                        // }}
                      >
                        <div className="m-12">
                          {(() => {
                            // Combine all items with their types
                            const allItems = [
                              ...(notes || []).map((item) => ({
                                ...item,
                                itemType: "notes",
                              })),
                              ...(flashcardSets || []).map((item) => ({
                                ...item,
                                itemType: "flashcardSets",
                              })),
                              ...(audioSummaries || []).map((item) => ({
                                ...item,
                                itemType: "audioSummaries",
                              })),
                              ...(quizzes || []).map((item) => ({
                                ...item,
                                itemType: "quizzes",
                              })),
                              ...(mindMaps || []).map((item) => ({
                                ...item,
                                itemType: "mindMaps",
                              })),
                            ];

                            // Sort by creation time (latest first)
                            const sortedItems = allItems.sort(
                              (a, b) =>
                                new Date(b.createdAt) - new Date(a.createdAt)
                            );

                            // Show only the latest 20 items
                            const displayItems = sortedItems.slice(0, 20);

                            if (displayItems.length === 0) {
                              return (
                                <span className="ah-text-2xs text-gray-01">
                                  No items yet. Create your first note,
                                  flashcard, audio summary, quiz, or mind map!
                                </span>
                              );
                            }

                            return displayItems.map((item) =>
                              renderItemCard(item, item.itemType)
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`position-absolute ${
                        isMobile ? "bottom-10" : "bottom-0"
                      }`}
                      style={{ left: "50%", transform: "translateX(-50%)" }}
                    >
                      <button
                        type="button"
                        className="btn btn-primary no-shadow py-10 mb-20 d-flex align-items-center gap-2 justify-content-center"
                        onClick={
                          hasSourceDocuments
                            ? handleAddNote
                            : showNoSourceToastMessage
                        }
                        disabled={
                          !hasSourceDocuments || isAnyGenerating
                        }
                        title={
                          hasSourceDocuments
                            ? "Add a new note"
                            : "No source documents available"
                        }
                        style={{
                          opacity: !hasSourceDocuments
                            ? 0.7
                            : isAnyGenerating
                            ? 0.5
                            : 1,
                          cursor:
                            !hasSourceDocuments || isAnyGenerating
                              ? "not-allowed"
                              : "pointer",
                          position: isMobile ? "relative" : "static",
                          marginBottom: isMobile ? "10px" : "20px",
                        }}
                      >
                        <AddNoteIcon />
                        Add Note
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="studio-items position-relative h-100">
                    <div className="row g-3">
                      <div className="col-12">
                        <div
                          className="bg-theme py-6 px-8 rounded-4 custom-tooltip"
                          data-title="Please expand to generate audio summary"
                          onClick={() => setRightCollapsed(false)}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            transition: "all 0.2s ease",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "0.8";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "0.6";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          <svg
                            width="20"
                            height="19"
                            viewBox="0 0 20 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.19057 18.0803V2.84197H8.88372V18.0803H7.19057ZM10.5769 14.694V6.22827H12.27V14.694H10.5769ZM3.80427 13.0009V7.92142H5.49742V13.0009H3.80427ZM13.9632 12.1543V8.768H15.6563V12.1543H13.9632ZM0.417969 11.3077V9.61457H2.11112V11.3077H0.417969ZM15.233 7.92142C15.2189 7.92142 15.1625 7.8791 15.0637 7.79444C14.838 6.93375 14.4111 6.18947 13.7833 5.5616C13.1554 4.93372 12.4111 4.5069 11.5504 4.28115C11.5222 4.26704 11.4799 4.2106 11.4234 4.11184C11.4234 4.08362 11.4658 4.02718 11.5504 3.94252C12.4111 3.71677 13.1554 3.28995 13.7833 2.66208C14.4111 2.0342 14.838 1.28992 15.0637 0.429235C15.0778 0.401016 15.1343 0.358688 15.233 0.30225C15.2612 0.30225 15.3177 0.344578 15.4023 0.429235C15.6422 1.28992 16.0725 2.0342 16.6934 2.66208C17.3142 3.28995 18.0549 3.71677 18.9156 3.94252C18.9438 3.94252 18.9862 3.99896 19.0426 4.11184C19.0426 4.12595 19.0003 4.18238 18.9156 4.28115C18.0549 4.5069 17.3107 4.93372 16.6828 5.5616C16.0549 6.18947 15.6281 6.93375 15.4023 7.79444C15.4023 7.82266 15.3459 7.86499 15.233 7.92142Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          className="bg-theme py-6 px-8 rounded-4 custom-tooltip"
                          data-title="Please expand to generate mind map"
                          onClick={() => setRightCollapsed(false)}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            transition: "all 0.2s ease",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "0.8";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "0.6";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          <svg
                            width="18"
                            height="15"
                            viewBox="0 0 18 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11.8023 14.3723V12.6792H8.41605V8.44629H6.7229V10.1394H0.796875V5.05999H6.7229V6.75314H8.41605V2.52027H11.8023V0.827117H17.7284V5.90657H11.8023V4.21342H10.1092V10.986H11.8023V9.29287H17.7284V14.3723H11.8023ZM13.4955 12.6792H16.0352V10.986H13.4955V12.6792ZM2.49002 8.44629H5.02975V6.75314H2.49002V8.44629ZM13.4955 4.21342H16.0352V2.52027H13.4955V4.21342Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          className="bg-theme py-6 px-8 rounded-4 custom-tooltip"
                          data-title="Please expand to generate flashcards"
                          onClick={() => setRightCollapsed(false)}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            transition: "all 0.2s ease",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "0.8";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "0.6";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8.95319 9.52296L10.8791 8.35892L12.8051 9.52296L12.2972 7.32186L13.9903 5.86152L11.768 5.67104L10.8791 3.59693L9.99024 5.67104L7.76798 5.86152L9.46113 7.32186L8.95319 9.52296ZM9.35531 15.0257H14.0961C13.9974 15.3925 13.828 15.6888 13.5882 15.9146C13.3483 16.1404 13.0379 16.2814 12.657 16.3379L3.42929 17.4596C2.96367 17.5301 2.54391 17.4208 2.17001 17.1315C1.7961 16.8423 1.58093 16.4649 1.52449 15.9993L0.402781 6.75043C0.346343 6.28481 0.45922 5.86858 0.741411 5.50173C1.0236 5.13488 1.39751 4.92323 1.86312 4.8668L2.83668 4.73981V6.43296L2.07477 6.53878L3.21764 15.7876L9.35531 15.0257ZM6.22298 13.3325C5.75737 13.3325 5.35877 13.1668 5.0272 12.8352C4.69562 12.5036 4.52983 12.105 4.52983 11.6394V2.32707C4.52983 1.86145 4.69562 1.46286 5.0272 1.13128C5.35877 0.799707 5.75737 0.633921 6.22298 0.633921H15.5353C16.0009 0.633921 16.3995 0.799707 16.7311 1.13128C17.0627 1.46286 17.2285 1.86145 17.2285 2.32707V11.6394C17.2285 12.105 17.0627 12.5036 16.7311 12.8352C16.3995 13.1668 16.0009 13.3325 15.5353 13.3325H6.22298ZM6.22298 11.6394H15.5353V2.32707H6.22298V11.6394Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          className="bg-theme py-6 px-8 rounded-4 custom-tooltip"
                          data-title="Please expand to generate quiz"
                          onClick={() => setRightCollapsed(false)}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            transition: "all 0.2s ease",
                            border: "1px solid rgba(255,255,255,0.2)",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "0.8";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "0.6";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.9577 11.6394C11.1976 11.6394 11.4057 11.5512 11.5821 11.3749C11.7584 11.1985 11.8466 10.9904 11.8466 10.7505C11.8466 10.5106 11.7584 10.3025 11.5821 10.1262C11.4057 9.94978 11.1976 9.8616 10.9577 9.8616C10.7179 9.8616 10.5097 9.94978 10.3334 10.1262C10.157 10.3025 10.0688 10.5106 10.0688 10.7505C10.0688 10.9904 10.157 11.1985 10.3334 11.3749C10.5097 11.5512 10.7179 11.6394 10.9577 11.6394ZM10.3228 8.93037H11.5927C11.5927 8.52119 11.635 8.22136 11.7196 8.03088C11.8043 7.8404 12.0018 7.58996 12.3122 7.27954C12.7355 6.85626 13.0177 6.5141 13.1588 6.25307C13.2999 5.99205 13.3705 5.68516 13.3705 5.33242C13.3705 4.69749 13.1482 4.17896 12.7038 3.77684C12.2593 3.37472 11.6773 3.17366 10.9577 3.17366C10.3792 3.17366 9.87482 3.33592 9.44447 3.66044C9.01413 3.98496 8.7143 4.4153 8.54499 4.95146L9.68786 5.41708C9.81485 5.06434 9.98769 4.79979 10.2064 4.62342C10.4251 4.44705 10.6755 4.35886 10.9577 4.35886C11.2964 4.35886 11.5715 4.4541 11.7831 4.64458C11.9948 4.83506 12.1006 5.09256 12.1006 5.41708C12.1006 5.61461 12.0442 5.80157 11.9313 5.97794C11.8184 6.15431 11.6209 6.37653 11.3387 6.64461C10.8731 7.05379 10.5874 7.37478 10.4815 7.60759C10.3757 7.8404 10.3228 8.28133 10.3228 8.93037ZM5.87828 14.1791C5.41266 14.1791 5.01407 14.0133 4.68249 13.6818C4.35092 13.3502 4.18513 12.9516 4.18513 12.486V2.32708C4.18513 1.86147 4.35092 1.46287 4.68249 1.13129C5.01407 0.799719 5.41266 0.633932 5.87828 0.633932H16.0372C16.5028 0.633932 16.9014 0.799719 17.233 1.13129C17.5645 1.46287 17.7303 1.86147 17.7303 2.32708V12.486C17.7303 12.9516 17.5645 13.3502 17.233 13.6818C16.9014 14.0133 16.5028 14.1791 16.0372 14.1791H5.87828ZM5.87828 12.486H16.0372V2.32708H5.87828V12.486ZM2.49198 17.5654C2.02636 17.5654 1.62777 17.3996 1.29619 17.0681C0.964616 16.7365 0.798828 16.3379 0.798828 15.8723V4.02023H2.49198V15.8723H14.344V17.5654H2.49198Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <hr />
                    {(() => {
                      // Combine all items with their types for collapsed view
                      const allItems = [
                        ...(notes || []).map((item) => ({
                          ...item,
                          itemType: "notes",
                        })),
                        ...(flashcardSets || []).map((item) => ({
                          ...item,
                          itemType: "flashcardSets",
                        })),
                        ...(audioSummaries || []).map((item) => ({
                          ...item,
                          itemType: "audioSummaries",
                        })),
                        ...(quizzes || []).map((item) => ({
                          ...item,
                          itemType: "quizzes",
                        })),
                        ...(mindMaps || []).map((item) => ({
                          ...item,
                          itemType: "mindMaps",
                        })),
                      ];

                      // Sort by creation time (latest first)
                      const sortedItems = allItems.sort(
                        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                      );

                      // Show only the latest 6 items in collapsed view
                      const displayItems = sortedItems.slice(0, 6);

                      if (displayItems.length === 0) {
                        return (
                          <div className="text-center py-4">
                            <span className="ah-text-2xs text-gray-01">
                              No items yet
                            </span>
                          </div>
                        );
                      }

                      return displayItems.map((item) => (
                        <div
                          key={`${item.itemType}-${item.id}`}
                          className="note-card p-5 mb-12 d-flex align-items-center justify-content-between custom-tooltip"
                          data-title={`Please expand to open ${
                            item.title || item.itemType
                          }`}
                          onClick={() => setRightCollapsed(false)}
                          style={{
                            cursor: "pointer",
                            opacity: 0.6,
                            transition: "opacity 0.2s ease",
                            borderRadius: "8px",
                            border: "1px solid #e8eaed",
                            background: "#f8f9fa",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <span
                              className="bg-white rounded-circle d-flex justify-content-center align-items-center note-icon"
                              style={{
                                width: "32px",
                                height: "32px",
                                minWidth: "32px",
                                minHeight: "32px",
                              }}
                            >
                              {noteCardsIcon[item.itemType]}
                            </span>
                          </div>
                        </div>
                      ));
                    })()}
                    <div className="position-absolute bottom-10 start-50 translate-middle-x ">
                      <button
                        type="button"
                        className="btn btn-primary no-shadow py-8 px-10 d-flex align-items-center gap-2 justify-content-center min-w-auto custom-tooltip"
                        data-title="Please expand to add note"
                        onClick={() => setRightCollapsed(false)}
                        style={{
                          cursor: "pointer",
                          opacity: 0.6,
                          transition: "opacity 0.2s ease",
                        }}
                      >
                        <AddNoteIcon />
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <div className="card-details">
                  {activeCard === "flashcards" && (
                    <div className="flashcards">
                      <h3 className="ah-heading-05 fw-semibold text-black-100">
                        Flashcards
                      </h3>
                      <span className="ah-text-xs fw-normal text-black-100">
                        Based on {currentFlashcardSet?.totalCards} cards
                      </span>
                      <hr />
                      <div
                        className="flashcard"
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          padding: "0 20px",
                        }}
                      >
                        <div className="slider-container d-flex justify-content-between align-items-center mb-15">
                          <div className="controls">
                            <button
                              type="button"
                              className="btn no-shadow prev-btn"
                              onClick={() => swiperRef.current?.slidePrev()}
                              disabled={activeIndex === 0}
                            >
                              <PrevButtonIcon />
                            </button>
                          </div>
                          <div
                            className="card-stack"
                            style={{
                              flex: 1,
                              maxWidth: "100%",
                              margin: "0 20px",
                            }}
                          >
                            <Swiper
                              effect={"cards"}
                              grabCursor={true}
                              modules={[EffectCards]}
                              className="mySwiper"
                              onSlideChange={(swiper) =>
                                setActiveIndex(swiper.activeIndex)
                              }
                              onSwiper={(swiper) =>
                                (swiperRef.current = swiper)
                              }
                            >
                              {currentFlashcards.map((item) => (
                                <SwiperSlide key={item.id}>
                                  <div
                                    className={`flash-card ${
                                      item.flipped ? "flipped" : ""
                                    }`}
                                  >
                                    <div className="card-inner text-white">
                                      <div className="card-front d-flex justify-content-between flex-column align-items-start py-37 px-16">
                                        <p className="fw-bolder ah-heading-03">
                                          {item.question}
                                        </p>
                                        <span
                                          className="rotate-button"
                                          onClick={() => toggleFlip(item.id)}
                                        >
                                          See Answer
                                        </span>
                                      </div>
                                      <div className="card-back py-37 px-16 d-flex justify-content-between flex-column align-items-start">
                                        <p className="fw-bolder ah-heading-03">
                                          {item.answer}
                                        </p>
                                        <span
                                          className="rotate-button"
                                          onClick={() => toggleFlip(item.id)}
                                        >
                                          See Question
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </SwiperSlide>
                              ))}
                            </Swiper>
                          </div>
                          <div className="controls">
                            <button
                              type="button"
                              className="btn no-shadow next-btn"
                              onClick={() => swiperRef.current?.slideNext()}
                              disabled={
                                activeIndex === currentFlashcards.length - 1
                              }
                            >
                              <NextButtonIcon />
                            </button>
                          </div>
                        </div>
                        <div className="text-center">
                          {activeIndex + 1} / {currentFlashcards.length}
                        </div>
                      </div>
                    </div>
                  )}
                  {activeCard === "mindmap" && (
                    <div
                      className="mindmap"
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        padding: "0 20px",
                      }}
                    >
                      <h3 className="ah-heading-05 fw-semibold text-black-100">
                        Mind Map
                      </h3>
                      <hr className="my-14" />
                      {currentMindMap ? (
                        <div style={{ width: "100%" }}>
                          <MindMap
                            data={currentMindMap}
                            title={currentMindMap.title}
                            description={currentMindMap.description}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 text-muted">Loading mind map...</p>
                        </div>
                      )}
                    </div>
                  )}
                  {activeCard === "quiz" && (
                    <div
                      className="quiz"
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        padding: "0 20px",
                      }}
                    >
                      <h3 className="ah-heading-05 fw-semibold text-black-100">
                        Brief Quiz
                      </h3>
                      <hr className="my-14" />
                      {currentQuiz ? (
                        <div style={{ width: "100%" }}>
                          <Quiz
                            courseId={courseId}
                            quizId={currentQuiz.id}
                            token={token}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 text-muted">Loading quiz...</p>
                        </div>
                      )}
                    </div>
                  )}
                  {activeCard === "audio" && (
                    <div
                      className="audio"
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        padding: "0 20px",
                      }}
                    >
                      <h3 className="ah-heading-05 fw-semibold text-black-100">
                        Audio Summary
                      </h3>
                      <hr className="my-14" />
                      {currentAudioSummary ? (
                        <div style={{ width: "100%" }}>
                          <AudioPlayer
                            content={currentAudioSummary.content}
                            title={currentAudioSummary.title}
                            durationEstimate={
                              currentAudioSummary.durationEstimate
                            }
                            voiceStyle={currentAudioSummary.voiceStyle}
                            language={currentAudioSummary.language}
                            className="mb-4"
                          />
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 text-muted">
                            Loading audio summary...
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {activeCard === "note" && (
                    <div
                      className="note"
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        padding: "0 20px",
                      }}
                    >
                      <h3 className="ah-heading-05 fw-semibold text-black-100">
                        {currentNoteId ? "Edit Note" : "Add New Note"}
                      </h3>
                      <hr className="my-14" />
                      <input
                        type="text"
                        className="form-control mb-12 bg-transparent"
                        placeholder="Note Title"
                        value={noteTitle}
                        onChange={(e) => setNoteTitle(e.target.value)}
                        style={{ width: "100%" }}
                      />
                      <textarea
                        name=""
                        className="form-control bg-transparent"
                        id=""
                        placeholder="Write Note Here....."
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        style={{
                          width: "100%",
                          minHeight: "200px",
                          resize: "vertical",
                        }}
                      ></textarea>
                      <div className="mt-15 mt-sm-30 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-primary no-shadow min-w-auto py-10 px-21"
                          onClick={handleSaveNote}
                        >
                          {currentNoteId ? "Update Note" : "Save Note"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentCourseNew;
